import { Button } from '@/components/tailwind/button'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/16/solid'
import { useReportsTranslation } from '@/i18n/hooks'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/16/solid'

export type FilterType = 'month' | 'quarter' | 'year'

export interface FilterState {
  type: FilterType
  year: number
  month?: number
  quarter?: number
}

interface BirthdayFiltersProps {
  filterState: FilterState
  onFilterChange: (newState: FilterState) => void
}

function BirthdayFilters({ filterState, onFilterChange }: BirthdayFiltersProps) {
  const { t } = useReportsTranslation()

  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth() + 1
  const currentQuarter = Math.ceil(currentMonth / 3)

  function handleTypeChange(newType: FilterType) {
    const baseState = { type: newType, year: filterState.year }

    switch (newType) {
      case 'month':
        onFilterChange({ ...baseState, month: filterState.month || currentMonth })
        break
      case 'quarter':
        onFilterChange({ ...baseState, quarter: filterState.quarter || currentQuarter })
        break
      case 'year':
        onFilterChange(baseState)
        break
    }
  }

  function canNavigateToPreviousMonth() {
    if (filterState.type !== 'month' || !filterState.month) return false
    const targetDate = new Date(filterState.year, filterState.month - 2) // month is 1-based
    const today = new Date()
    return targetDate >= new Date(today.getFullYear(), today.getMonth())
  }

  function navigateMonth(direction: 'prev' | 'next') {
    if (filterState.type !== 'month' || !filterState.month) return

    let newMonth = filterState.month
    let newYear = filterState.year

    if (direction === 'prev') {
      newMonth = newMonth === 1 ? 12 : newMonth - 1
      if (newMonth === 12) newYear--
    } else {
      newMonth = newMonth === 12 ? 1 : newMonth + 1
      if (newMonth === 1) newYear++
    }

    onFilterChange({ ...filterState, month: newMonth, year: newYear })
  }

  function getMonthName(month: number) {
    const monthKeys = [
      'january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december'
    ]
    return t(`birthdays.months.${monthKeys[month - 1]}`)
  }

  function getQuarterName(quarter: number) {
    const quarterKeys = ['q1', 'q2', 'q3', 'q4']
    return t(`birthdays.quarters.${quarterKeys[quarter - 1]}`)
  }

  function renderMonthSelector() {
    if (filterState.type !== 'month') return null

    return (
      <div className="flex items-center gap-2">
        <Button
          outline
          onClick={() => navigateMonth('prev')}
          disabled={!canNavigateToPreviousMonth()}
          aria-label={t('birthdays.navigation.previousMonth')}
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </Button>

        <div className="w-[140px] text-center font-medium text-gray-900 dark:text-gray-100">
          {filterState.month && `${getMonthName(filterState.month)} ${filterState.year}`}
        </div>

        <Button
          outline
          onClick={() => navigateMonth('next')}
          aria-label={t('birthdays.navigation.nextMonth')}
        >
          <ChevronRightIcon className="h-4 w-4" />
        </Button>
      </div>
    )
  }

  function canNavigateToPreviousQuarter() {
    if (filterState.type !== 'quarter' || !filterState.quarter) return false
    const targetQuarter = filterState.quarter === 1 ? 4 : filterState.quarter - 1
    const targetYear = filterState.quarter === 1 ? filterState.year - 1 : filterState.year
    return targetYear > currentYear || (targetYear === currentYear && targetQuarter >= currentQuarter)
  }

  function navigateQuarter(direction: 'prev' | 'next') {
    if (filterState.type !== 'quarter' || !filterState.quarter) return

    let newQuarter = filterState.quarter
    let newYear = filterState.year

    if (direction === 'prev') {
      newQuarter = newQuarter === 1 ? 4 : newQuarter - 1
      if (newQuarter === 4) newYear--
    } else {
      newQuarter = newQuarter === 4 ? 1 : newQuarter + 1
      if (newQuarter === 1) newYear++
    }

    onFilterChange({ ...filterState, quarter: newQuarter, year: newYear })
  }

  function renderQuarterSelector() {
    if (filterState.type !== 'quarter') return null

    return (
      <div className="flex items-center gap-2">
        <Button
          outline
          onClick={() => navigateQuarter('prev')}
          disabled={!canNavigateToPreviousQuarter()}
          aria-label={t('birthdays.navigation.previousQuarter')}
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </Button>

        <div className="w-[140px] text-center font-medium text-gray-900 dark:text-gray-100">
          {filterState.quarter && filterState.year && `${getQuarterName(filterState.quarter)} ${filterState.year}`}
        </div>

        <Button
          outline
          onClick={() => navigateQuarter('next')}
          aria-label={t('birthdays.navigation.nextQuarter')}
        >
          <ChevronRightIcon className="h-4 w-4" />
        </Button>
      </div>
    )
  }

  function canNavigateToPreviousYear() {
    if (filterState.type !== 'year') return false
    return filterState.year - 1 >= currentYear
  }

  function navigateYear(direction: 'prev' | 'next') {
    if (filterState.type !== 'year') return

    const newYear = direction === 'prev'
      ? filterState.year - 1
      : filterState.year + 1

    onFilterChange({ ...filterState, year: newYear })
  }

  function renderYearSelector() {
    if (filterState.type !== 'year') return null

    return (
      <div className="flex items-center gap-2">
        <Button
          outline
          onClick={() => navigateYear('prev')}
          disabled={!canNavigateToPreviousYear()}
          aria-label={t('birthdays.navigation.previousYear')}
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </Button>

        <div className="w-[140px] text-center font-medium text-gray-900 dark:text-gray-100">
          {filterState.year}
        </div>

        <Button
          outline
          onClick={() => navigateYear('next')}
          aria-label={t('birthdays.navigation.nextYear')}
        >
          <ChevronRightIcon className="h-4 w-4" />
        </Button>
      </div>
    )
  }

  return (
    <div className="w-full relative mb-6 flex justify-end">
      <div className="flex gap-2 items-center">
        {/* Filter Type Selector */}
        <Menu as="div" className="relative">
          <MenuButton className="inline-flex justify-center items-center px-3 py-2 text-sm font-medium rounded-md border shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 ease-in-out bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700">
            {filterState.type === 'month' && t('birthdays.filters.month')}
            {filterState.type === 'quarter' && t('birthdays.filters.quarter')}
            {filterState.type === 'year' && t('birthdays.filters.year')}
            <ChevronDownIcon className="ml-2 h-4 w-4" />
          </MenuButton>
          <MenuItems className="absolute right-0 z-10 mt-2 w-32 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800 dark:ring-gray-700">
            <MenuItem>
              {({ active }) => (
                <button
                  onClick={() => handleTypeChange('month')}
                  className={`block w-full px-4 py-2 text-left text-sm ${
                    active ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {t('birthdays.filters.month')}
                </button>
              )}
            </MenuItem>
            <MenuItem>
              {({ active }) => (
                <button
                  onClick={() => handleTypeChange('quarter')}
                  className={`block w-full px-4 py-2 text-left text-sm ${
                    active ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {t('birthdays.filters.quarter')}
                </button>
              )}
            </MenuItem>
            <MenuItem>
              {({ active }) => (
                <button
                  onClick={() => handleTypeChange('year')}
                  className={`block w-full px-4 py-2 text-left text-sm ${
                    active ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {t('birthdays.filters.year')}
                </button>
              )}
            </MenuItem>
          </MenuItems>
        </Menu>

        {/* Period Navigation/Selection */}
        {renderMonthSelector()}
        {renderQuarterSelector()}
        {renderYearSelector()}
      </div>
    </div>
  )
}

export default BirthdayFilters